<script>
import { mapActions, mapState, mapGetters } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import AlertDescription from "@/pages/Alerts/Alert/Description";
import CardTableLinkIndicator from "@/components/CardTableLinkIndicator";

import {
  formatHashrate,
  formatDuration,
  formatRelativeDate,
  deferSortingToController,
} from "@/utils";

// Number of alerts to show on each page
const PAGE_SIZE = 15;

export default {
  // Included components
  components: {
    NavigationWrapper,
    CardTableLinkIndicator,
    AlertDescription,
  },

  data() {
    return {
      // The current page index (1-indexed)
      currentPage: null,

      // The current sort (either "created", "name", or "last_triggered")
      sortBy: "created",

      // Whether to sort descending (true: descending, false: ascending)
      sortDesc: false,
    };
  },

  computed: {
    // The number of pages to display
    pageCount() {
      return Math.ceil(Object.values(this.alerts).length / PAGE_SIZE);
    },

    // The current page of alerts
    page() {
      // Make a shallow-copy of the cached alerts
      let alerts = Object.values(this.alerts);

      // Sort that copy based on sortBy and sortDesc
      alerts.sort((a, b) => {
        if (b[this.sortBy] == null || a[this.sortBy] > b[this.sortBy]) {
          return this.sortDesc ? -1 : 1;
        } else if (a[this.sortBy] === b[this.sortBy]) {
          return 0;
        } else {
          return this.sortDesc ? 1 : -1;
        }
      });

      // Return a slice of the sorted alerts, yielding a single page
      return alerts.slice(
        (this.currentPage - 1) * PAGE_SIZE,
        this.currentPage * PAGE_SIZE
      );
    },

    // Copies of alert.enabled to allow for indirect manipulation
    enabledShadows() {
      let enabledShadows = {};
      Object.values(this.alerts).forEach((alert) => {
        enabledShadows[alert.id] = alert.enabled;
      });
      return enabledShadows;
    },

    // Bind the state's alerts to this controller
    ...mapGetters(["can"]),
    ...mapState(["alerts"]),
  },

  // When the component is first loaded, fetch the alerts
  async created() {
    await this.fetchAlerts();
  },

  methods: {
    // Fetch the team's alerts
    async fetchAlerts() {
      // Get the current page number from the URL, or default to 1
      this.currentPage = this.$route.query.page || 1;

      // Get the alerts from the API
      await this.getAlerts({});
    },

    // Go to the details page for the alert
    goToAlert(alert) {
      this.$router.push({ name: "alert", params: { alertId: alert.id } });
    },

    // Toggle the alert (enabled vs. disabled)
    toggleAlert(alert) {
      if (this.enabledShadows[alert.id]) {
        this.enableAlert({ alertId: alert.id });
      } else {
        this.disableAlert({ alertId: alert.id });
      }
    },

    // Other helper functions and actions
    formatRelativeDate,
    formatHashrate,
    formatDuration,
    deferSortingToController,
    ...mapActions(["getAlerts", "enableAlert", "disableAlert"]),
  },
};
</script>

<template>
  <NavigationWrapper entity="alerts" action="view">
    <template #breadcrumbs>
      <b-breadcrumb-item to="/alerts" active>Alerts</b-breadcrumb-item>
    </template>
    <b-row class="mb-2">
      <b-col>
        <h1>Alerts</h1>
      </b-col>
      <b-col
        align-self="center"
        v-if="can.alerts.edit && !(Object.keys(alerts).length === 0)"
      >
        <b-button class="float-right" variant="primary" to="/alerts/create">
          Add Alert
        </b-button>
      </b-col>
    </b-row>

    <b-row v-if="Object.values(alerts).length === 0">
      <b-col>
        <b-card class="text-center py-5">
          <b-card-title>No configured alerts</b-card-title>
          <b-card-text>
            Configure alerts to notify you when miners underperform
          </b-card-text>
          <b-button
            v-if="can.alerts.edit"
            variant="primary"
            to="/alerts/create"
          >
            Add Alert
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-if="Object.values(alerts).length !== 0">
      <b-col>
        <b-card class="Card-table Card-table-hoverable mb-3">
          <b-table
            responsive
            small
            :fields="[
              {
                key: 'name',
                label: 'Name',
                sortable: true,
                thStyle: { width: '30%' },
              },
              {
                key: 'description',
                label: 'Description',
                thStyle: { width: '40%' },
              },
              {
                key: 'last_triggered',
                label: 'Triggered',
                sortable: true,
                formatter(value, key, item) {
                  return formatRelativeDate(value);
                },
                thStyle: { width: '18%' },
              },
              {
                key: 'triggering',
                label: 'Status',
                sortable: true,
              },
              {
                key: 'enabled',
                label: 'Enabled',
              },
            ]"
            :items="page"
            @row-clicked="goToAlert"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="deferSortingToController"
          >
            <template #cell(name)="{ item }">
              <b-link :to="`/alerts/${item.id}`">{{ item.name }}</b-link>
              <CardTableLinkIndicator />
            </template>

            <template #cell(description)="{ item }">
              <AlertDescription :alert="item" />
            </template>

            <template #cell(triggering)="{ item }">
              <b-badge v-if="!item.enabled" key="null">PAUSED</b-badge>
              <b-badge v-else-if="item.triggering" key="true" variant="danger">
                ALERT
              </b-badge>
              <b-badge v-else variant="success" key="false">OK</b-badge>
            </template>

            <template #cell(enabled)="{ item }">
              <b-form-checkbox
                :key="item.id"
                size="lg"
                v-model="enabledShadows[item.id]"
                @change="toggleAlert(item)"
                switch
              ></b-form-checkbox>
            </template>
          </b-table>
        </b-card>
        <b-pagination-nav
          v-if="pageCount > 1"
          use-router
          base-url="?page="
          v-model="currentPage"
          size="sm"
          :number-of-pages="pageCount"
          :hide-goto-end-buttons="pageCount < 3"
          align="center"
          limit="7"
        ></b-pagination-nav>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped></style>
